import { Autocomplete, Box, FormControl, FormControlLabel, FormHelperText, FormLabel, Radio, RadioGroup, TextField, Tooltip, Typography } from "@mui/material";
import { useState, useEffect, useRef } from 'react';
import TooltipNumber from "./basicData/TooltipNumber";
import TooltipInvoiceType from "./basicData/TooltipInvoiceType";
import BasicDataMessageError from "./basicData/BasicDataMessageError";
import { getResourcePlacesSearch } from "../../api/resourcePlaces";
import { getWorksSearch } from "../../api/works";
import ErrorBox from "../../../components/shared/boxes/ErrorBox";
import { getExternalAddress } from "../../api/external_address";
import { getCustomerLastInvoice } from "../../api/invoices";

const InvoiceBasicData = ({ data, setData, onValidityChange, hasWorksSelected, accountId }) => {
    const [basicDataError, setBasicDataError] = useState(false);
    const [clientOptions, setClientOptions] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [loadingClients, setLoadingClients] = useState(false);
    const [configApiError, setconfigApiError] = useState(null);
    const [ZIPCodeError, setZIPCodeError] = useState(false);
    const zipFromLastInvoice = useRef(false);

    const hasClient = !!data.client;
    const ZIPCodeValue = data.ZIPCode || "";
    const PureZipValue = ZIPCodeValue.length >= 8 ? ZIPCodeValue.replace(/\D/g, "") : ZIPCodeValue;
    const previousClientId = useRef(null);

    useEffect(() => {
        if (!accountId) {
            setClientOptions([]);
            return;
        }

        const fetchClients = async () => {
            setLoadingClients(true);
            try {
                const params = { viewMode: ['Customer'], accountId };
                if (searchText && searchText.trim() !== '') {
                    params.q = searchText.trim();
                }

                const result = await getResourcePlacesSearch(params);

                const options = result.results
                    .map(item => ({ ...item, label: item.name }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                setClientOptions(options);
            } catch (error) {
                setconfigApiError(error.message);
            } finally {
                setLoadingClients(false);
            }
        };

        if (searchText === '') {
            fetchClients();
        } else {
            const timeoutId = setTimeout(fetchClients, 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [searchText, accountId]);

    const resetAddress = (prev) => ({
        ...prev,
        ZIPCode: "",
        city: "",
        FU: "",
        streetAddress: "",
        numberAddress: "",
        district: "",
        complementAddress: "",
    });


    useEffect(() => {
        if (!data.client?.id) {
            setBasicDataError(false);
            return;
        }

        const fetchWorks = async () => {
            try {
                const result = await getWorksSearch({ rootIds: [data.client.id] });
                const worksData = Array.isArray(result?.results) ? result.results : [];

                setBasicDataError(worksData.length === 0);
            } catch (err) {
                setBasicDataError(true);
            }
        };

        fetchWorks();
    }, [data.client]);

    useEffect(() => {
        let isCurrent = true;
        const currentClientId = data.client?.id;

        if (!currentClientId) {
            setData(resetAddress);
            previousClientId.current = null;
            return;
        }

        const clientChanged =
            previousClientId.current &&
            previousClientId.current !== currentClientId;

        if (clientChanged) {
            setData(resetAddress);
        }

        previousClientId.current = currentClientId;

        const fetchLastInvoice = async () => {
            try {
                const result = await getCustomerLastInvoice(data.client.id);

                if (!isCurrent) return;

                const lastInvoice = result?.results?.[0];
                if (!lastInvoice) return;

                zipFromLastInvoice.current = true;

                setData(prev => ({
                    ...prev,
                    ZIPCode: lastInvoice.zip_code_address,
                    city: lastInvoice.city_address,
                    FU: lastInvoice.state_address,
                    streetAddress: lastInvoice.street_address,
                    numberAddress: lastInvoice.number_address,
                    district: lastInvoice.district_address,
                    complementAddress: lastInvoice.complement_address || "",
                }));
            } catch (error) {
                setconfigApiError(error.message);
            }
        };

        fetchLastInvoice();

        return () => {
            isCurrent = false;
        };
    }, [data.client]);

    useEffect(() => {
        if (zipFromLastInvoice.current) {
            zipFromLastInvoice.current = false;
            return;
        }

        if (PureZipValue.length !== 8) {
            setData((prevState) => ({
                ...prevState,
                city: "",
                FU: "",
                streetAddress: "",
                district: "",
            }));
            setZIPCodeError(false);
            return;
        }

        const fetchExternalAddress = async () => {
            try {
                const result = await getExternalAddress({ q: PureZipValue });
                setData((prevState) => ({
                    ...prevState,
                    city: result.localidade || "",
                    FU: result.uf || "",
                    streetAddress: result.logradouro || "",
                    district: result.bairro || "",
                }));
                if (result.erro === "true") {
                    setZIPCodeError(true);
                }
            } catch (error) {
                setZIPCodeError(true);
            }
        };

        fetchExternalAddress();
    }, [data.ZIPCode, PureZipValue]);


    useEffect(() => {
        const hasClient = !!data.client;

        const isValid = hasClient && !!data?.invoiceType && (data.invoiceType === "without_work" || !basicDataError) && !configApiError &&
            !!data.ZIPCode && !!data.city && !!data.FU && !!data.streetAddress && !!data.numberAddress && !!data.district;

        onValidityChange?.(isValid);
    }, [data, basicDataError, configApiError, onValidityChange]);

    return (
        <Box sx={{ p: '3rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
            {basicDataError && data.invoiceType === 'has_work' && (
                <BasicDataMessageError />
            )}
            {configApiError && (
                <ErrorBox
                    errorMessage={configApiError}
                    onClose={() => setconfigApiError(null)}
                />
            )}
            <Box>
                <TooltipNumber />
                <Tooltip title="Não é possível alterar o número da fatura" placement='right' arrow>
                    <TextField disabled placeholder="-" variant="outlined" />
                </Tooltip>
            </Box>
            <Box>
                <FormLabel sx={{ color: 'text.primary' }}><b> Cliente </b><FormLabel sx={{ color: 'error.main' }} ><b>*</b></FormLabel></FormLabel>
                <Tooltip
                    title={hasWorksSelected ? "Não é possível alterar essa informação porque já existem itens vinculados à fatura." : null}
                    placement="right"
                    arrow
                    disableHoverListener={!hasWorksSelected}
                >
                    <Autocomplete
                        onInputChange={(e, newInputValue) => setSearchText(newInputValue)}
                        disablePortal
                        disabled={hasWorksSelected}
                        options={clientOptions}
                        getOptionLabel={(option) => option?.label || ""}
                        value={data.client || null}
                        onChange={(e, newValue) => setData((prevState) => ({ ...prevState, client: newValue, }))}
                        sx={{ width: 300 }}
                        slotProps={{
                            listbox: {
                                sx: {
                                    maxHeight: '12rem',
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Pesquisar"
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props}>{option.label}</li>
                        )}
                        noOptionsText={!loadingClients ? "Nenhum resultado encontrado" : "Carregando..."}
                    />
                </Tooltip>
            </Box>
            {hasClient && (
                <>
                    <Box sx={{ display: 'flex', gap: '1.5rem' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FormLabel sx={{ color: 'text.primary' }}><b> CEP </b><FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>
                            <TextField
                                type="text"
                                variant="outlined"
                                placeholder='00000000'
                                error={ZIPCodeError}
                                slotProps={{
                                    htmlInput: {
                                        maxLength: 9
                                    }
                                }}
                                value={data.ZIPCode}
                                onChange={(e) => {
                                    const value = e.target.value;

                                    if (/^[0-9-]*$/.test(value)) {
                                        setData((prevState) => ({
                                            ...prevState,
                                            ZIPCode: value,
                                        }));
                                    }
                                }}
                                sx={{
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                    },
                                    '& input[type=number]::-webkit-outer-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                }}
                            />
                            {ZIPCodeError && (
                                <FormHelperText variant="helperError" sx={{ px: '0.875rem' }}>
                                    CEP inválido
                                </FormHelperText>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FormLabel sx={{ color: 'text.primary' }}><b> Cidade </b><FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>
                            <Box>
                                <Tooltip title="Insira um CEP válido para preencher este campo automaticamente" placement="right" arrow>
                                    <TextField
                                        sx={{ minWidth: '16.875rem' }}
                                        disabled
                                        variant="outlined"
                                        placeholder='-'
                                        value={data.city}
                                    />
                                </Tooltip>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FormLabel sx={{ color: 'text.primary' }}><b> UF </b><FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>
                            <Box>
                                <Tooltip title="Insira um CEP válido para preencher este campo automaticamente" placement="right" arrow>
                                    <TextField
                                        sx={{ maxWidth: '4.65rem' }}
                                        disabled
                                        variant="outlined"
                                        placeholder='-'
                                        value={data.FU}
                                    />
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '1.5rem' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FormLabel sx={{ color: 'text.primary' }}><b> Logradouro </b><FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>
                            <TextField
                                sx={{ minWidth: '23rem' }}
                                variant="outlined"
                                placeholder='Ex: Rua, Avenida...'
                                value={data.streetAddress}
                                onChange={(e) => setData((prevState) => ({ ...prevState, streetAddress: e.target.value, }))}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FormLabel sx={{ color: 'text.primary' }}><b> Número </b><FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>
                            <TextField
                                variant="outlined"
                                placeholder='Ex: 123'
                                value={data.numberAddress}
                                onChange={(e) => setData((prevState) => ({ ...prevState, numberAddress: e.target.value, }))}
                            />
                            <Typography variant="body3" sx={{ color: 'text.secondary', p: '0.2rem 0.875rem 0 0.875rem', }}>Informar "S/N" se não houver.</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '1.5rem' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <FormLabel sx={{ color: 'text.primary' }}><b> Bairro </b><FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>
                            <TextField
                                sx={{ minWidth: '16.875rem' }}
                                variant="outlined"
                                placeholder='Ex: Centro'
                                value={data.district}
                                onChange={(e) => setData((prevState) => ({ ...prevState, district: e.target.value, }))}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', }}>
                            <FormLabel sx={{ color: 'text.primary', pb: '0.3125rem' }}><b> Complemento </b></FormLabel>
                            <TextField
                                sx={{ minWidth: '16.875rem' }}
                                variant="outlined"
                                placeholder='Ex: Apto, Bloco, Sala...'
                                value={data.complementAddress}
                                onChange={(e) => setData((prevState) => ({ ...prevState, complementAddress: e.target.value, }))}
                            />
                        </Box>
                    </Box>
                </>
            )}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormLabel sx={{ color: 'text.primary' }}><b> Tipo de fatura </b><FormLabel sx={{ color: 'error.main' }}><b>*</b></FormLabel></FormLabel>
                    <TooltipInvoiceType />
                </Box>
                <Box>
                    <Tooltip
                        title={hasWorksSelected ? "Não é possível alterar essa informação porque já existem itens vinculados à fatura." : null}
                        placement="right"
                        arrow
                        disableHoverListener={!hasWorksSelected}
                    >
                        <FormControl>
                            <RadioGroup
                                aria-labelledby="invoice-type-label"
                                name="invoice-type"
                                value={data.invoiceType || ""}
                                onChange={(e) =>
                                    setData((prevState) => ({
                                        ...prevState,
                                        invoiceType: e.target.value,
                                    })
                                    )}
                            >

                                <FormControlLabel value="has_work" control={<Radio />} disabled={hasWorksSelected} label="Com vínculo" />
                                <FormControlLabel value="without_work" control={<Radio />} disabled={hasWorksSelected} label="Sem vínculo" />
                            </RadioGroup>
                        </FormControl>
                    </Tooltip>
                </Box>
            </Box>
            <Box>
                <Typography variant='caption' sx={{ color: 'error.main' }}><b>*</b> Indica um campo obrigatório </Typography>
            </Box>
        </Box>

    )
}

export default InvoiceBasicData;
